import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { InferType } from 'yup'
import { useQueries } from '@tanstack/react-query'
import { FormControl } from '../../../../../../components/common/FormControl'
import { useFormErrorMessage } from '../../../../../../hooks/useFormErrorMessage'
import {
  Button,
  ButtonVariant,
} from '../../../../../../components/common/Button'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { adminSetMasterAdminSchema } from '../../../../../../forms/schemas/admin_set_master_admin'
import { SelectDropdown } from '../../../../../../components/common/SelectDropdown'
import { ISocietiesModel } from '../../../../../../interfaces/models/societies.interfaces'
import { userQueries } from '../../../../../../api/users'
import { getUserFullName } from '../../../../../../helpers/user'

interface SetMasterAdminModalContentProps {
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof adminSetMasterAdminSchema>) => Promise<void>
  onClose: () => void
  society?: ISocietiesModel
  adminsList?: string[]
}

export const SetMasterAdminModalContent = ({
  onError,
  onSubmit,
  onClose,
  society,
  adminsList,
}: SetMasterAdminModalContentProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()

  const currentMasterAdminUserId = society?.masterAdminUserId

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(adminSetMasterAdminSchema),
    defaultValues: {
      masterAdminUserId: currentMasterAdminUserId || '',
    },
  })

  const adminUsersQuery = useQueries({
    queries:
      adminsList?.map((adminId) => {
        return userQueries.getUser(adminId)
      }) || [],
  })

  const admins = adminUsersQuery.flatMap((query) => query.data || [])
  const adminsValues =
    admins?.map((admin) => {
      return {
        value: admin._id,
        label: getUserFullName(admin) || '',
      }
    }) || []

  return (
    <form
      className="flex h-full flex-col gap-6"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Controller
        control={control}
        name="masterAdminUserId"
        render={({ field: { value, name, onChange } }) => (
          <FormControl
            name={name}
            error={
              errors.masterAdminUserId &&
              getErrorMessage(errors.masterAdminUserId)
            }
          >
            <SelectDropdown
              options={adminsValues}
              value={adminsValues.find((option) => option.value === value)}
              onChange={(option) => onChange(option?.value)}
              error={!!errors.masterAdminUserId}
            />
          </FormControl>
        )}
      />
      <div className="flex flex-wrap justify-end gap-4">
        <Button label={translate('common.actions.cancel')} onClick={onClose} />
        <Button
          variant={ButtonVariant.PRIMARY}
          disabled={!isValid}
          label={translate('common.actions.save')}
          type="submit"
        />
      </div>
    </form>
  )
}
